import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Download } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { useBuildings } from '@/hooks'
import { useOrganization } from '@/hooks'
import { api } from '@/services'
import {
  PageHeader,
  EmptyState,
  DataTable,
  SortableHeader,
  BulkQRDownloadDialog,
  Forbidden,
} from '@/components'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { slugify } from '@/lib/qr'
import type { Building, Room } from '@/types'
import { useOrgRooms } from './useOrgRooms'

interface DialogState {
  open: boolean
  rooms: Room[]
  buildings: Building[]
  zipName: string
}

const CLOSED_DIALOG: DialogState = {
  open: false,
  rooms: [],
  buildings: [],
  zipName: '',
}

/**
 * Top-level Exports page. Currently renders the QR codes panel only —
 * future panels (compliance report exports, CSV data exports) will land
 * here but are not rendered until they exist.
 */
export function ExportsPage() {
  const { user } = useAuth()
  const { effectiveOrgId, selectedOrg } = useOrg()
  const buildingsQuery = useBuildings(effectiveOrgId)
  // When a super_admin has selected an org, selectedOrg already carries the
  // name. Otherwise fetch by the effective org id. Empty string disables the
  // query inside useOrganization.
  const orgQuery = useOrganization(selectedOrg ? '' : effectiveOrgId)
  const orgRoomsQuery = useOrgRooms(effectiveOrgId)

  const [dialog, setDialog] = useState<DialogState>(CLOSED_DIALOG)
  const [loadingBuildingId, setLoadingBuildingId] = useState<string | null>(null)
  const [loadingAll, setLoadingAll] = useState(false)

  const orgName =
    selectedOrg?.name ?? orgQuery.data?.name ?? 'organization'
  const orgSlug = slugify(orgName) || 'organization'

  const columns = useMemo<ColumnDef<Building>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <SortableHeader column={column}>Building</SortableHeader>
        ),
        cell: ({ row }) => (
          <span className="font-medium">{row.getValue('name')}</span>
        ),
      },
      {
        id: 'room_count',
        header: 'Rooms',
        cell: ({ row }) => {
          const b = row.original
          const count =
            orgRoomsQuery.data?.rooms.filter((r) => r.building_id === b.id)
              .length ?? null
          return (
            <span className="text-muted-foreground">
              {count === null ? '…' : count}
            </span>
          )
        },
      },
      {
        id: 'actions',
        cell: ({ row }) => {
          const b = row.original
          const pending = loadingBuildingId === b.id
          return (
            <Button
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={() => openDialogForBuilding(b)}
            >
              <Download className="mr-2 h-4 w-4" />
              {pending ? 'Loading…' : 'Download'}
            </Button>
          )
        },
      },
    ],
    [orgRoomsQuery.data, loadingBuildingId]
  )

  // Admin gate. Canonical pattern across the app is in-page <Forbidden />
  // (see CompliancePage, OrganizationSettingsPage, BrandingSettingsPage,
  // OrganizationsPage, OrgDetailPage). Sidebar already hides this item
  // for non-admins; this guard covers direct-URL navigation. Placed
  // after all hook calls to satisfy rules-of-hooks.
  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return <Forbidden />
  }

  async function openDialogForBuilding(building: Building) {
    setLoadingBuildingId(building.id)
    try {
      // Use the same endpoint useRooms does, fresh per click so the ZIP
      // always reflects current data.
      const rooms = await api.get<Room[]>(`/buildings/${building.id}/rooms`)
      setDialog({
        open: true,
        rooms,
        buildings: [building],
        zipName: `${slugify(building.name) || 'building'}-qr-codes.zip`,
      })
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load rooms'
      toast.error(message)
    } finally {
      setLoadingBuildingId(null)
    }
  }

  async function openDialogForAll() {
    if (!effectiveOrgId) return
    setLoadingAll(true)
    try {
      // Prefer the already-fetched query data to avoid a duplicate round-trip,
      // but fall back to an explicit refetch if it hasn't loaded yet.
      const data = orgRoomsQuery.data ?? (await orgRoomsQuery.refetch()).data
      if (!data) return
      setDialog({
        open: true,
        rooms: data.rooms,
        buildings: data.buildings,
        zipName: `${orgSlug}-qr-codes.zip`,
      })
    } finally {
      setLoadingAll(false)
    }
  }

  const downloadAllDisabled = !effectiveOrgId
  const totalBuildings = buildingsQuery.data?.length ?? 0
  const totalRooms = orgRoomsQuery.data?.rooms.length ?? null

  return (
    <div className="space-y-6">
      <PageHeader
        title="Exports"
        description="Download QR codes and other exports for your organization."
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">QR codes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Per-org row */}
          <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-4">
            <div>
              <p className="font-medium">All rooms in {orgName}</p>
              <p className="text-sm text-muted-foreground">
                {totalBuildings} {totalBuildings === 1 ? 'building' : 'buildings'}
                {' · '}
                {totalRooms === null ? '…' : totalRooms}{' '}
                {totalRooms === 1 ? 'room' : 'rooms'}
              </p>
            </div>
            <span
              title={
                downloadAllDisabled ? 'Select an organization first' : undefined
              }
            >
              <Button
                disabled={downloadAllDisabled || loadingAll}
                onClick={openDialogForAll}
              >
                <Download className="mr-2 h-4 w-4" />
                {loadingAll ? 'Loading…' : 'Download all'}
              </Button>
            </span>
          </div>

          {/* Per-building table */}
          {buildingsQuery.isLoading ? (
            <div className="text-muted-foreground">Loading buildings...</div>
          ) : buildingsQuery.error ? (
            <div className="text-destructive">
              Failed to load buildings: {buildingsQuery.error.message}
            </div>
          ) : buildingsQuery.data && buildingsQuery.data.length > 0 ? (
            <DataTable
              columns={columns}
              data={buildingsQuery.data}
              filterColumn="name"
              filterPlaceholder="Filter buildings..."
            />
          ) : (
            <EmptyState
              title="No buildings"
              description="Add buildings before downloading QR codes."
            />
          )}
        </CardContent>
      </Card>

      <BulkQRDownloadDialog
        open={dialog.open}
        onOpenChange={(open) =>
          setDialog((prev) => ({ ...prev, open }))
        }
        rooms={dialog.rooms}
        buildings={dialog.buildings}
        zipName={dialog.zipName}
      />
    </div>
  )
}
